/**
 * Tenant detail > Overview: the tenant's feature plan and its billing plan side by side
 * (DEC-366 Q12: the billing plan does not set the features in v1, so both are shown).
 *
 * The only warning in v1 is a customer billed with no billing plan in force: none, or the
 * latest one has ended (Session 6, 2026-10-19; #940 review LOW-2). A plan-vs-features mismatch
 * warning needs a rate card's feature template, which arrives with migration 0232; no guess
 * from names is made before then.
 *
 * Reads only this customer (#940 review LOW-4): its account and its subscriptions, newest
 * first. A tenant Paelode does not bill reads nothing.
 */
import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { listFrom } from '../../lib/url-state';
import { Pill, subText } from '../../ui/bits';
import { useHaulerPlan } from '../tenants/plan-hooks';
import { useBillingAccount, useBillingToday, useSubscriptions } from './billing-hooks';
import {
  BILLING_STATUS_LABELS,
  PLAN_GAP_LABELS,
  billedWithoutPlan,
  subscriptionLine,
  planMismatch,
  templateLabel,
} from './billing-format';

const BILLED_TYPES = new Set(['HAULER', 'OPERATOR', 'DISPOSAL']);

function HaulerFeaturePlan({ tenantId }: { tenantId: string }): React.JSX.Element {
  const { data, isLoading } = useHaulerPlan(tenantId);
  if (isLoading || data === undefined) return <span style={subText}>…</span>;
  return <span data-testid="tenant-feature-plan">{data.plan.template}</span>;
}

/** The card sells one hauler plan, the hauler runs on another: invoices and features disagree. */
function PlanMismatch({
  tenantId,
  cardTemplate,
}: {
  tenantId: string;
  cardTemplate: string;
}): React.JSX.Element | null {
  const { data } = useHaulerPlan(tenantId);
  const m = planMismatch(cardTemplate, data?.plan.template);
  if (m === null) return null;
  return (
    <div role="alert" data-testid="plan-mismatch" style={{ gridColumn: '1 / -1' }}>
      <Pill tone="warning" label="Plan mismatch" />{' '}
      <span style={subText}>
        {`Billed on a rate card for the ${templateLabel(m.card)} plan, but this hauler's features run on ${templateLabel(m.hauler)}. Change the billing plan or the feature plan so they match.`}
      </span>
    </div>
  );
}

function BillingCardBody({
  tenantId,
  tenantType,
}: {
  tenantId: string;
  tenantType: string;
}): React.JSX.Element {
  const account = useBillingAccount(tenantId);
  const subs = useSubscriptions(tenantId);
  const today = useBillingToday();

  if (account.isLoading || subs.isLoading) return <p style={subText}>Loading…</p>;
  if (account.isError || subs.isError || account.data === undefined || subs.data === undefined) {
    return <p style={subText}>Billing details are not available.</p>;
  }
  const latest = subs.data.subscriptions[0] ?? null;
  const row = { billingStatus: account.data.billingStatus, latestSubscription: latest };
  const gap = today === null ? null : billedWithoutPlan(row, today);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
      {tenantType === 'HAULER' && (
        <div>
          <div style={subText}>Feature plan</div>
          <HaulerFeaturePlan tenantId={tenantId} />
        </div>
      )}
      <div>
        <div style={subText}>Billing plan</div>
        <span data-testid="tenant-billing-plan">{subscriptionLine(latest)}</span>
      </div>
      <div>
        <div style={subText}>Billing</div>
        <Pill
          tone={row.billingStatus === 'ACTIVE' ? 'positive' : 'muted'}
          label={BILLING_STATUS_LABELS[row.billingStatus]}
        />
      </div>
      {tenantType === 'HAULER' &&
        latest !== null &&
        latest.rateCardFeatureTemplate !== undefined &&
        latest.rateCardFeatureTemplate !== null && (
          <PlanMismatch tenantId={tenantId} cardTemplate={latest.rateCardFeatureTemplate} />
        )}
      {gap !== null && (
        <div role="alert" data-testid="billed-without-plan" style={{ gridColumn: '1 / -1' }}>
          <Pill tone="warning" label={PLAN_GAP_LABELS[gap]} />{' '}
          <span style={subText}>
            {gap === 'NO_PLAN'
              ? 'This customer is billed but has no billing plan in force, so its invoices would be empty.'
              : 'This plan ends before the last day of the month, and invoices bill the plan in force on that day, so this month’s invoice would be empty.'}
          </span>
        </div>
      )}
    </div>
  );
}

export function TenantBillingCard({
  tenantId,
  tenantType,
}: {
  tenantId: string;
  tenantType: string;
}): React.JSX.Element | null {
  const location = useLocation();
  // Paelode bills haulers, operators and disposal companies; any other tenant shows nothing.
  if (!BILLED_TYPES.has(tenantType)) return null;
  return (
    <section
      data-testid="tenant-billing-card"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
        marginBottom: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>Plan and billing</h3>
        {/* OPEN-191 — the customer page's Back returns to this tenant. */}
        <Link
          to={`/billing/customers/${tenantId}`}
          state={listFrom(location)}
          style={{ fontSize: '13px' }}
        >
          Open billing
        </Link>
      </div>
      <BillingCardBody tenantId={tenantId} tenantType={tenantType} />
    </section>
  );
}
